import { motion } from 'framer-motion'
import { AddFundsButton } from './AddFundsButton'

const COIN = ({ delay = 0 }: { delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 0, scale: 0.5 }}
    animate={{ opacity: [0, 1, 1, 0], y: -36, scale: [0.5, 1, 1, 0.8] }}
    transition={{ duration: 1.8, delay, repeat: Infinity, repeatDelay: 1.2, ease: 'easeOut' }}
    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
  >
    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-[10px] font-bold text-white shadow-lg shadow-amber-500/40">
      $
    </div>
  </motion.div>
)

const ARROW = ({ delay = 0 }: { delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, scaleX: 0 }}
    animate={{ opacity: [0, 1, 1, 0], scaleX: [0, 1, 1, 0] }}
    transition={{ duration: 1.2, delay, repeat: Infinity, repeatDelay: 1.8, ease: 'easeInOut' }}
    className="flex items-center"
    style={{ originX: 0 }}
  >
    <div className="w-8 h-px bg-gradient-to-r from-cyan-400/40 to-cyan-400" />
    <div className="border-t-[3px] border-b-[3px] border-l-[5px] border-transparent border-l-cyan-400 border-t-transparent border-b-transparent" />
  </motion.div>
)

function SocialCard({ icon, name, color, delay }: {
  icon: React.ReactNode
  name: string
  color: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ y: -4, scale: 1.06 }}
      className="relative flex flex-col items-center gap-2 rounded-2xl border border-slate-700/60 bg-slate-800/60 backdrop-blur-sm px-3 py-3 w-[78px]"
      style={{ boxShadow: `0 0 18px ${color}15` }}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center"
        style={{ background: `${color}20`, border: `1px solid ${color}35` }}
      >
        {icon}
      </div>
      <span className="text-[10px] font-medium text-slate-400">{name}</span>
      <motion.div
        animate={{ scale: [1, 1.6], opacity: [0.35, 0] }}
        transition={{ duration: 1.6, repeat: Infinity, delay }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ border: `1px solid ${color}50` }}
      />
    </motion.div>
  )
}

const SOCIALS = [
  {
    name: 'Gmail',
    color: '#EA4335',
    delay: 0.1,
    coinDelay: 0.2,
    arrowDelay: 0.4,
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
    ),
  },
  {
    name: 'Twitter',
    color: '#1d9bf0',
    delay: 0.2,
    coinDelay: 0.8,
    arrowDelay: 1.0,
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current text-sky-400">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    name: 'Telegram',
    color: '#2AABEE',
    delay: 0.3,
    coinDelay: 1.4,
    arrowDelay: 1.6,
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current text-blue-400">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
      </svg>
    ),
  },
  {
    name: 'Discord',
    color: '#5865F2',
    delay: 0.4,
    coinDelay: 0,
    arrowDelay: 0,
    icon: (
      <svg viewBox="0 0 127.14 96.36" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
        <path fill="#5865F2" d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z"/>
      </svg>
    ),
  },
]

export function SocialPaymentIllustration() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="w-full flex flex-col items-center gap-4"
    >
      {/* Icons row */}
      <div className="flex items-center gap-2 flex-wrap justify-center">
        {SOCIALS.map((s, i) => (
          <div key={s.name} className="flex items-center gap-2">
            <SocialCard icon={s.icon} name={s.name} color={s.color} delay={s.delay} />
            {i < SOCIALS.length - 1 && (
              <div className="relative h-8 flex items-center">
                <COIN delay={s.coinDelay} />
                <ARROW delay={s.arrowDelay} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add funds */}
      <AddFundsButton />

      {/* powered by */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-[10px] text-cyan-300 font-medium">powered by Arc Network</span>
      </motion.div>
    </motion.div>
  )
}
